import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState } from "@/components/common";
import { ReportDateFilter } from "@/components/ReportDateFilter";
import { VoiceFunnelLedger } from "@/components/VoiceFunnelLedger";
import { VoiceProductFunnel } from "@/components/VoiceProductFunnel";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend,
} from "recharts";
import { PhoneCall, Users, TrendingUp, HelpCircle, Sparkles, CalendarClock, ListChecks } from "lucide-react";

const BOOKING_METHOD_OPTIONS = [
  ["", "All methods"], ["ai_direct", "AI Voice — direct"], ["ai_assisted", "AI Voice — assisted"],
  ["self_booking", "Self-booking"], ["employee", "Employee/manual"], ["leadership_admin", "Leadership/admin"],
];

const Stat = ({ label, value, hint, testId }) => (
  <div className="space-y-0.5" data-testid={testId}>
    <p className="text-[11px] text-muted-foreground">{label}</p>
    <p className="font-heading text-xl font-semibold tabular-nums">{value ?? "—"}</p>
    {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
  </div>
);

const Pct = ({ v }) => <span className="tabular-nums">{v == null ? "—" : `${v}%`}</span>;

/* Reusable drill-down: any headline number is clickable, opens this,
   fetches the matching opportunity/attempt rows from the ONE shared
   backend endpoint every category (funnel stage, AI metric, why-not-
   booked reason, Anika issue) routes through. */
export const DrilldownDialog = ({ open, setOpen, title, category, filters }) => {
  const [rows, setRows] = useState(null);
  // R14: the backend now reports total / offset / limit / truncated / unit, so the dialog can say "Showing X of Y" and
  // load further pages instead of silently stopping at the first 500.
  const [meta, setMeta] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  useEffect(() => {
    if (!open || !category) return;
    setRows(null);
    setMeta(null);
    const p = new URLSearchParams({ category, ...filters });
    api.get(`/reports/voice-automation/drilldown?${p.toString()}`)
      .then((r) => { setRows(r.data.rows); setMeta(r.data); })
      .catch((e) => { toast.error(errDetail(e)); setRows([]); });
  }, [open, category, filters]);

  const loadMore = async () => {
    if (!meta || loadingMore) return;
    setLoadingMore(true);
    try {
      const p = new URLSearchParams({ category, ...filters, offset: String(meta.offset + meta.limit), limit: String(meta.limit) });
      const { data } = await api.get(`/reports/voice-automation/drilldown?${p.toString()}`);
      setRows((prev) => [...(prev || []), ...data.rows]);
      setMeta(data);
    } catch (e) { toast.error(errDetail(e)); } finally { setLoadingMore(false); }
  };
  const total = meta?.total;
  const shownSoFar = rows ? rows.length : 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="voice-automation-drilldown">
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription data-testid="drilldown-count">
            {!rows ? "Loading…"
              : total != null
                ? `Showing ${shownSoFar} of ${total} ${meta?.unit === "attempts" ? (total === 1 ? "attempt" : "attempts") : (total === 1 ? "opportunity" : "opportunities")}${meta?.truncated ? " — more available below" : ""}`
                : `${rows.length} record${rows.length === 1 ? "" : "s"}`}
          </DialogDescription>
        </DialogHeader>
        {!rows ? (
          <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : rows.length === 0 ? (
          <EmptyState icon={HelpCircle} title="No matching records" />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Source / campaign</TableHead>
                  <TableHead>Attempt</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Booking origin</TableHead>
                  <TableHead>Summary / reason</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={`${r.opportunity_id}-${r.voice_attempt_id || i}`} data-testid={`drilldown-row-${i}`}>
                    <TableCell>
                      <div className="font-medium text-sm">{r.customer_name || "—"}</div>
                      <div className="text-[11px] font-mono text-muted-foreground">{r.customer_id}</div>
                    </TableCell>
                    <TableCell className="text-sm">{r.product_name || r.product_code}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.source || "—"}{r.campaign ? ` · ${r.campaign}` : ""}</TableCell>
                    <TableCell className="text-xs">{r.attempt_no ?? "—"}{r.call_duration_secs != null ? ` · ${r.call_duration_secs}s` : ""}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px] capitalize">{(r.outcome || "—").replace(/_/g, " ")}</Badge></TableCell>
                    <TableCell className="text-xs capitalize">{(r.booking_origin || "—").replace(/_/g, " ")}</TableCell>
                    <TableCell className="max-w-64 text-xs text-muted-foreground">
                      {r.transcript_summary || r.customer_reason || (r.agent_issues || []).join(", ") || (r.analysis_status === "not_generated" ? "Analysis not generated" : "—")}
                    </TableCell>
                    <TableCell>
                      {r.customer_360_link && (
                        <Link to={r.customer_360_link} className="text-xs underline decoration-dotted hover:text-primary">Customer 360</Link>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {meta?.truncated && (
              <div className="pt-3 flex justify-center">
                <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="drilldown-load-more">
                  {loadingMore ? "Loading…" : `Show next ${meta.limit}`}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const FunnelBar = ({ stage, max, onClick }) => (
  <button data-testid={`funnel-stage-${stage.key}`} onClick={onClick}
    className="w-full text-left rounded-md border p-2.5 hover:border-primary/50 transition-colors">
    <div className="flex items-center justify-between text-sm">
      <span className="font-medium">{stage.label}</span>
      <span className="font-heading text-lg font-semibold tabular-nums">{stage.count}</span>
    </div>
    <div className="mt-1.5 h-2 rounded-full bg-muted overflow-hidden">
      <div className="h-full bg-primary" style={{ width: `${max ? Math.max(2, (stage.count / max) * 100) : 0}%` }} />
    </div>
    <div className="mt-1 text-[10px] text-muted-foreground flex gap-3">
      {stage.pct_of_previous != null && <span>{stage.pct_of_previous}% of previous stage</span>}
      <span>{stage.pct_of_total}% of cohort</span>
    </div>
  </button>
);

export const VoiceAutomationReport = () => {
  const { hasCap } = useAuth();
  // 2026-09 Phase 2 (F2) — same reusable date-range filter the rest of
  // Reports uses; this report already had a date_from/date_to-capable
  // backend (voice_reports.parse_date_range), just no way to reach it
  // from the UI before now.
  const [dateFilter, setDateFilter] = useState({ preset: "30", days: "30", date_from: null, date_to: null });
  const [product, setProduct] = useState("all");
  const [products, setProducts] = useState([]);
  const [source, setSource] = useState("");
  const [campaign, setCampaign] = useState("");
  const [bookingMethod, setBookingMethod] = useState("");
  const [includeTest, setIncludeTest] = useState(false);
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null); // {title, category}
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const dateOnlyParams = dateFilter.date_from || dateFilter.date_to
    ? { date_from: dateFilter.date_from || "", date_to: dateFilter.date_to || "" }
    : { days: dateFilter.days || 30 };
  const filterParams = {
    ...dateOnlyParams, ...(product !== "all" ? { product_code: product } : {}),
    ...(source ? { source } : {}), ...(campaign ? { campaign } : {}),
    ...(bookingMethod ? { booking_method: bookingMethod } : {}),
    ...(includeTest ? { include_test: "true" } : {}),
  };
  const drillFilterParams = { ...dateOnlyParams, ...(product !== "all" ? { product_code: product } : {}),
    ...(source ? { source } : {}), ...(campaign ? { campaign } : {}),
    ...(includeTest ? { include_test: "true" } : {}) };

  const load = useCallback(() => {
    setData(null);
    const p = new URLSearchParams(filterParams);
    api.get(`/reports/voice-automation?${p.toString()}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errDetail(e, "Could not load report")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, product, source, campaign, bookingMethod, includeTest]);
  useEffect(() => { load(); }, [load]);

  const openDrill = (title, category) => setDrill({ title, category });

  const runBackfillPreview = async () => {
    try {
      const { data: d } = await api.post("/reports/voice-automation/analysis/backfill-preview?limit=20");
      setBackfillPreview(d);
    } catch (e) { toast.error(errDetail(e)); }
  };
  const runBackfillExecute = async () => {
    setBackfillBusy(true);
    try {
      const { data: d } = await api.post("/reports/voice-automation/analysis/backfill-execute?limit=20");
      toast.success(`Analyzed ${d.analyzed}, unavailable ${d.unavailable}, skipped ${d.skipped}`);
      setBackfillPreview(null);
      load();
    } catch (e) { toast.error(errDetail(e)); } finally { setBackfillBusy(false); }
  };

  if (!data) {
    return <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  }

  const maxFunnel = Math.max(...data.funnel.map((s) => s.count), 1);
  // R13 Workstream 3: the ledger takes the same date / product / TEST filters as the cohort report
  // (source, campaign and booking method are opportunity-cohort concepts and do not apply to it).
  const ledgerParams = { ...dateOnlyParams, ...(product !== "all" ? { product_code: product } : {}),
    ...(includeTest ? { include_test: "true" } : {}) };
  const ai = data.ai_voice;
  const conv = data.conversion_rates;
  const sb = data.self_booking;

  return (
    <div className="space-y-4" data-testid="voice-automation-report">
      <div className="flex items-center gap-2 flex-wrap">
        <ReportDateFilter preset={dateFilter.preset} onChange={setDateFilter} testIdPrefix="va-date" />
        <Select value={product} onValueChange={setProduct}>
          <SelectTrigger data-testid="va-product-filter" className="h-9 w-44 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All products</SelectItem>
            {products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <input data-testid="va-source-filter" className="h-9 rounded-md border bg-background px-2 text-xs w-32"
          placeholder="Source" value={source} onChange={(e) => setSource(e.target.value)} />
        <input data-testid="va-campaign-filter" className="h-9 rounded-md border bg-background px-2 text-xs w-32"
          placeholder="Campaign" value={campaign} onChange={(e) => setCampaign(e.target.value)} />
        <Select value={bookingMethod || "__all"} onValueChange={(v) => setBookingMethod(v === "__all" ? "" : v)}>
          <SelectTrigger data-testid="va-booking-method-filter" className="h-9 w-48 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{BOOKING_METHOD_OPTIONS.map(([v, l]) => <SelectItem key={v || "__all"} value={v || "__all"}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} /> Include TEST
        </label>
      </div>
      <VoiceFunnelLedger params={ledgerParams} />
      <VoiceProductFunnel params={{ ...dateOnlyParams, ...(includeTest ? { include_test: "true" } : {}) }} />

      <p className="text-xs text-muted-foreground" data-testid="va-cohort-note">
        Cohort: <b>{data.cohort_size}</b> opportunities received in this period (<b>{data.unique_customers}</b> unique customers) —
        what happened to THIS cohort, regardless of when it later converted.
      </p>

      {/* F — overall funnel */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-2"><ListChecks className="h-4 w-4" />Overall automation funnel</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {data.funnel.map((s) => (
            <FunnelBar key={s.key} stage={s} max={maxFunnel} onClick={() => openDrill(s.label, s.key)} />
          ))}
        </CardContent>
      </Card>

      {/* G — AI Voice performance */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-2"><PhoneCall className="h-4 w-4" />AI Voice (Anika) performance</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat testId="va-eligible" label="Eligible opportunities" value={ai.eligible_opportunities} />
            <Stat testId="va-unique-customers" label="Unique customers attempted" value={ai.unique_customers_attempted} />
            <Stat label="Total Voice attempts" value={ai.total_attempts} />
            <Stat label="Avg attempts / customer" value={ai.avg_attempts_per_customer} />
            <Stat testId="va-dispatched" label="Provider-dispatched calls" value={ai.overall.provider_dispatched}
              hint={`accepted by the provider${ai.overall.accepted_result_missing ? `; ${ai.overall.accepted_result_missing} accepted but no result was ever received` : ""}${ai.overall.accepted_awaiting_result ? `; ${ai.overall.accepted_awaiting_result} still awaiting a result` : ""}`} />
            <Stat label="Pre-dispatch failures" value={ai.overall.pre_dispatch_failed} />
            <Stat label="Provider result unknown" value={ai.overall.provider_result_unknown}
              hint="Excluded from pickup-rate denominator" />
            <Stat label="No-answer" value={ai.overall.no_answer} />
            <button className="text-left" onClick={() => openDrill("Reached (picked up)", "reached")} data-testid="va-reached">
              <Stat label="Reached (picked up)" value={`${ai.overall.reached} (${ai.overall.reach_rate_pct ?? "—"}%)`}
                hint="of dispatched calls with a known provider result" />
            </button>
            <button className="text-left" onClick={() => openDrill("Meaningful conversations", "meaningful_conversation")} data-testid="va-meaningful">
              <Stat label="Meaningful conversations" value={`${ai.overall.meaningful_conversations} (${ai.overall.meaningful_rate_pct ?? "—"}%)`} />
            </button>
            <Stat label="Avg meaningful call duration" value={ai.overall.avg_meaningful_duration_secs != null ? `${ai.overall.avg_meaningful_duration_secs}s` : "—"} />
            <button className="text-left" onClick={() => openDrill("AI directly booked", "ai_direct_booked")} data-testid="va-ai-direct">
              <Stat label="AI directly booked" value={ai.ai_direct_booked_opportunities} />
            </button>
            <button className="text-left" onClick={() => openDrill("AI-assisted / human-confirmed", "ai_assisted_booked")} data-testid="va-ai-assisted">
              <Stat label="AI-assisted / human-confirmed" value={ai.ai_assisted_booked_opportunities} />
            </button>
            <button className="text-left" onClick={() => openDrill("Meaningful — no booking", "meaningful_no_booking")} data-testid="va-meaningful-no-booking">
              <Stat label="Meaningful, no booking" value={ai.meaningful_no_booking_opportunities} />
            </button>
            <Stat label="Callback requested" value={ai.overall.callback_requested} />
            <Stat label="Human requested" value={ai.overall.human_requested} />
            <Stat label="Human follow-up required" value={ai.human_followup_required} />
            <Stat label="Declined" value={ai.overall.declined} />
            <Stat label="Explicit takeover from AI" value={ai.explicit_takeover_from_ai} />
          </div>

          <div>
            <p className="text-xs font-semibold mb-1.5">By attempt number</p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Attempt</TableHead><TableHead>Count</TableHead><TableHead>Reach rate</TableHead>
                  <TableHead>Meaningful rate</TableHead><TableHead>Directly booked</TableHead><TableHead>Conversion</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(ai.attempts_by_number || []).map((r) => (
                    <TableRow key={`n${r.attempt_no}`} data-testid={`va-attempt-${r.attempt_no}-row`}>
                      <TableCell>#{r.attempt_no}</TableCell><TableCell>{r.count}</TableCell>
                      <TableCell><Pct v={r.reach_rate_pct} /></TableCell>
                      <TableCell><Pct v={r.meaningful_rate_pct} /></TableCell>
                      <TableCell>{r.booked_direct}</TableCell>
                      <TableCell><Pct v={r.conversion_pct} /></TableCell>
                    </TableRow>
                  ))}
                  {(ai.attempts_by_number || []).length === 0 && (
                    <TableRow data-testid="va-attempts-empty"><TableCell colSpan={6} className="text-xs text-muted-foreground">No ordinary attempts in this period.</TableCell></TableRow>
                  )}
                  {ai.unnumbered_ordinary_attempts?.count > 0 && (
                    <TableRow data-testid="va-attempt-unnumbered-row">
                      <TableCell>No number recorded</TableCell><TableCell>{ai.unnumbered_ordinary_attempts.count}</TableCell>
                      <TableCell><Pct v={ai.unnumbered_ordinary_attempts.reach_rate_pct} /></TableCell>
                      <TableCell><Pct v={ai.unnumbered_ordinary_attempts.meaningful_rate_pct} /></TableCell>
                      <TableCell>{ai.unnumbered_ordinary_attempts.booked_direct}</TableCell><TableCell>—</TableCell>
                    </TableRow>
                  )}
                  <TableRow data-testid="va-attempt-callback-row" className="bg-muted/40">
                    <TableCell>Callbacks (separate)</TableCell><TableCell>{ai.callback_attempts?.count ?? 0}</TableCell>
                    <TableCell><Pct v={ai.callback_attempts?.reach_rate_pct} /></TableCell>
                    <TableCell><Pct v={ai.callback_attempts?.meaningful_rate_pct} /></TableCell>
                    <TableCell>{ai.callback_attempts?.booked_direct ?? 0}</TableCell>
                    <TableCell><Pct v={ai.callback_attempts?.conversion_pct} /></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            {ai.attempt_reconciliation && (
              <p data-testid="va-attempt-reconciliation" className="text-[11px] text-muted-foreground mt-1">
                {ai.attempt_reconciliation.ordinary_numbered} numbered ordinary + {ai.attempt_reconciliation.ordinary_unnumbered} unnumbered + {ai.attempt_reconciliation.callbacks} callback
                = {ai.attempt_reconciliation.total} attempts ({ai.attempt_reconciliation.reconciles ? "adds up" : "DOES NOT add up"}).
                {data.attempt_policy && ` Current ordinary budget: ${data.attempt_policy.ordinary_attempt_budget_now}. ${data.attempt_policy.note}`}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* H — conversion rates */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-2"><TrendingUp className="h-4 w-4" />AI Voice conversion rates</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Stat label="AI direct / eligible" value={<Pct v={conv.ai_direct_booked_per_eligible_pct} />} />
          <Stat label="AI direct / customers attempted" value={<Pct v={conv.ai_direct_booked_per_customer_attempted_pct} />} />
          <Stat label="AI direct / reached" value={<Pct v={conv.ai_direct_booked_per_reached_pct} />} />
          <Stat label="AI direct / meaningful conversations" value={<Pct v={conv.ai_direct_booked_per_meaningful_pct} />} />
          <Stat label="AI direct + assisted / meaningful conversations" value={<Pct v={conv.ai_direct_plus_assisted_per_meaningful_pct} />} />
        </CardContent>
      </Card>

      {/* Breakdowns */}
      <div className="grid md:grid-cols-3 gap-3">
        {[["breakdown_by_product", "By product"], ["breakdown_by_source", "By source"], ["breakdown_by_campaign", "By campaign"]].map(([k, label]) => (
          <Card key={k}><CardHeader className="pb-2"><CardTitle className="text-xs font-heading">{label}</CardTitle></CardHeader>
            <CardContent className="space-y-1.5">
              {(data[k] || []).slice(0, 8).map((g) => (
                <div key={g.key} className="flex items-center justify-between text-xs">
                  <span className="truncate max-w-32">{g.key}</span>
                  <span className="tabular-nums text-muted-foreground">{g.ai_direct_booked}/{g.opportunities} (<Pct v={g.ai_direct_conversion_pct} />)</span>
                </div>
              ))}
              {(!data[k] || data[k].length === 0) && <p className="text-xs text-muted-foreground">No data in this period</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* I / J / K / L — meaningful-but-not-booked analysis */}
      <div className="grid md:grid-cols-2 gap-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Top customer reasons for not booking</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {data.why_not_booked.top_customer_reasons.length === 0 && (
              <p className="text-xs text-muted-foreground">No analysed conversations in this period yet.</p>
            )}
            {data.why_not_booked.top_customer_reasons.map((r) => (
              <button key={r.key} data-testid={`va-reason-${r.key}`} className="w-full flex items-center justify-between text-xs hover:text-primary"
                onClick={() => openDrill(r.label, `customer_reason:${r.key}`)}>
                <span>{r.label}</span><Badge variant="outline" className="text-[10px]">{r.count}</Badge>
              </button>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Top Anika improvement opportunities</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {data.why_not_booked.top_agent_issues.length === 0 && (
              <p className="text-xs text-muted-foreground">No agent-side issues identified in this period.</p>
            )}
            {data.why_not_booked.top_agent_issues.map((r) => (
              <button key={r.key} data-testid={`va-issue-${r.key}`} className="w-full flex items-center justify-between text-xs hover:text-primary"
                onClick={() => openDrill(r.label, `agent_issue:${r.key}`)}>
                <span>{r.label}</span><Badge variant="outline" className="text-[10px]">{r.count}</Badge>
              </button>
            ))}
            <p className="text-[10px] text-muted-foreground pt-1">
              {data.why_not_booked.meaningful_not_booked_conversations} meaningful, unconverted conversations in this period ·{" "}
              {data.why_not_booked.analysis_not_generated} analysis not yet generated
            </p>
          </CardContent>
        </Card>
      </div>

      {hasCap("admin.config") && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs font-heading flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" />Historical transcript-quality backfill (Admin)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-muted-foreground">Bounded and explicit — never automatic. Preview first, then run a small batch.</p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="va-backfill-preview" onClick={runBackfillPreview}>Preview (20)</Button>
              {backfillPreview && (
                <Button size="sm" className="h-7 text-xs" data-testid="va-backfill-execute" disabled={backfillBusy || !backfillPreview.will_analyze_up_to} onClick={runBackfillExecute}>
                  {backfillBusy ? "Analyzing…" : `Analyze ${backfillPreview.will_analyze_up_to}`}
                </Button>
              )}
            </div>
            {backfillPreview && (
              <p className="text-[11px] text-muted-foreground" data-testid="va-backfill-preview-result">
                Scanned {backfillPreview.scanned} recent resolved attempts — {backfillPreview.eligible_in_scanned_window} eligible for analysis.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* N — self-booking performance */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-2"><CalendarClock className="h-4 w-4" />Self-booking performance</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Booking invitations generated" value={sb.booking_invitations_generated} />
          <Stat label="Interakt invitations dispatched" value={sb.interakt_invitations_dispatched} />
          <button className="text-left" onClick={() => openDrill("Offered self-booking", "self_booking_offered")} data-testid="va-sb-offered">
            <Stat label="Opportunities offered self-booking" value={sb.opportunities_offered_self_booking} />
          </button>
          <button className="text-left" onClick={() => openDrill("Self-booking completed", "self_booking_completed")} data-testid="va-sb-completed">
            <Stat label="Self-booking completed" value={`${sb.self_booking_completed} (${sb.self_booking_conversion_pct ?? "—"}%)`} />
          </button>
          <Stat label="Avg hours receipt → self-booked" value={sb.avg_hours_receipt_to_self_booking ?? "—"} />
          <Stat label="Attended" value={sb.attended} />
          <Stat label="No-show" value={sb.no_show} />
          <Stat label="Cancelled" value={sb.cancelled} />
        </CardContent>
      </Card>

      {/* O — AI vs self-booking vs human comparison */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading flex items-center gap-2"><Users className="h-4 w-4" />AI vs self-booking vs human</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Channel</TableHead><TableHead>Exposed</TableHead><TableHead>Booked</TableHead>
              <TableHead>Conversion</TableHead><TableHead>Avg hours to booking</TableHead>
              <TableHead>Attendance</TableHead><TableHead>No-show</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.comparison.map((row) => (
                <TableRow key={row.key} data-testid={`va-compare-${row.key}`}>
                  <TableCell className="font-medium text-sm">{row.label}</TableCell>
                  <TableCell>{row.exposed ?? "—"}{row.exposed_note && <span className="text-[9px] text-muted-foreground block">{row.exposed_note}</span>}</TableCell>
                  <TableCell>{row.consultations_booked}</TableCell>
                  <TableCell><Pct v={row.conversion_pct} /></TableCell>
                  <TableCell>{row.avg_hours_to_booking ?? "—"}</TableCell>
                  <TableCell><Pct v={row.attendance_rate_pct} /></TableCell>
                  <TableCell><Pct v={row.no_show_rate_pct} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {data.comparison_totals && (
            <p data-testid="va-compare-totals" className="text-[11px] text-muted-foreground mt-2">
              {data.comparison_totals.distinct_booked_opportunities} distinct opportunities booked; the rows add up to {data.comparison_totals.sum_of_channel_rows}
              {data.comparison_totals.rows_overlap ? ` because ${data.comparison_totals.opportunities_booked_through_more_than_one_channel} opportunit${data.comparison_totals.opportunities_booked_through_more_than_one_channel === 1 ? "y was" : "ies were"} booked through more than one channel.` : " (no overlap)."}
            </p>
          )}
        </CardContent>
      </Card>

      {/* P — trends */}
      {data.trends.length > 0 && (
        <Card data-testid="va-trends-chart">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Trends (activity occurring within the period)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data.trends} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="period" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="attempts" name="Voice attempts" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.15} strokeWidth={2} />
                <Area type="monotone" dataKey="reached" name="Reached" stroke="#2e9e77" fill="transparent" strokeWidth={2} />
                <Area type="monotone" dataKey="meaningful" name="Meaningful" stroke="#7c6af0" fill="transparent" strokeWidth={2} />
                <Area type="monotone" dataKey="ai_booked" name="AI booked (direct+assisted)" stroke="#e0a83c" fill="transparent" strokeWidth={2} />
                <Area type="monotone" dataKey="self_booked" name="Self-booked" stroke="#4aa3df" fill="transparent" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <div className="space-y-1">
        {data.data_notes.map((n, i) => <p key={i} className="text-[10px] text-muted-foreground">· {n}</p>)}
      </div>

      <DrilldownDialog open={!!drill} setOpen={(o) => !o && setDrill(null)} title={drill?.title}
        category={drill?.category} filters={drillFilterParams} />
    </div>
  );
};
